// ABOUTME: Defines Zod schemas for TechLog fuel and fluid uplift API data.
import { ZdateTime } from '../common.zod';
import { TechlogTruckUnit } from './fuel';
import { z } from 'zod';

// Fluid uplift schema for oil/hydraulic uplifts - GET /v1/sectors/{id}/fluid-uplifts
export const ZTechlogFluidUplift = z.object({
  id: z.string(),
  category: z.string().nullable(),
  adjustment: z.number().nullable(),
  uplift: z.number().nullable().describe('Uplift amount in upliftUnit'),
  associatedRecordId: z.string(),
  associatedRecordType: z.string(),
  fluidType: z.enum(['oil', 'hydraulic']),
  // TechLog can return null for fluidDestination in fluid uplift responses.
  fluidDestination: z.enum(['engine', 'apu', 'idg', 'bug']).nullable(),
  fluidDestinationNumber: z.number().nullable(),
  upliftUnit: z.enum(['ml', 'qt']),
  aircraftId: z.string(),
  initialValue: z.number().nullable(),
  departureValue: z.number().nullable(),
  hydraulicCircuitId: z.string().nullable(),
  attachments: z.array(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// FlightFuelSummaryResponse schema
export const ZFlightFuelSummaryResponse = z.object({
  preUpliftFuel: z
    .number()
    .describe(
      "Amount of fuel in the aircraft before fueling (in aircraft's fuel unit)",
    ),
  postUpliftFuel: z
    .number()
    .describe(
      "Amount of fuel in the aircraft after fueling (in aircraft's fuel unit)",
    ),
  usedFuel: z
    .number()
    .describe(
      "Amount of fuel consumed during the flight (in aircraft's fuel unit)",
    ),
  remainingFuel: z
    .number()
    .describe(
      "Amount of fuel remaining after the flight (in aircraft's fuel unit)",
    ),
});

// TechlogFuelUplifts schema
export const ZTechlogFuelUplifts = z.object({
  id: z.string().describe('Unique identifier for the fuel uplift record'),
  associatedRecordId: z
    .string()
    .describe('ID of the associated record (usually a flight/sector ID)'),
  associatedRecordType: z
    .string()
    .describe('Type of the associated record (e.g., "Flight")'),
  aircraftId: z.string().describe('ID of the aircraft that received the fuel'),
  aircraftValuePreUplift: z
    .number()
    .nullable()
    .optional()
    .describe(
      "Fuel quantity in the aircraft before fueling (in aircraft's unit)",
    ),
  aircraftValuePostUplift: z
    .number()
    .nullable()
    .optional()
    .describe(
      "Fuel quantity in the aircraft after fueling (in aircraft's unit)",
    ),
  aircraftValueActualUplift: z
    .number()
    .nullable()
    .optional()
    .describe(
      "Actual fuel quantity added to the aircraft (in aircraft's unit)",
    ),
  aircraftActualUplift: z
    .number()
    .nullable()
    .optional()
    .describe(
      'Actual uplift recorded against the aircraft when provided separately from aircraftValueActualUplift',
    ),
  aircraftPlannedUplift: z
    .number()
    .nullable()
    .optional()
    .describe('Planned uplift recorded directly on the aircraft uplift record'),
  truckActualUplift: z
    .number()
    .nullable()
    .optional()
    .describe("Actual fuel quantity delivered by the truck (in truck's unit)"),
  truckUnit: z
    .nativeEnum(TechlogTruckUnit)
    .nullable()
    .optional()
    .describe(
      'Unit of measurement used by the fuel truck (e.g., "usg" for US Gallons, "l" for liters)',
    ),
  density: z
    .number()
    .nullable()
    .optional()
    .describe(
      'Fuel density value used for conversions between mass and volume units',
    ),
  fuelType: z
    .string()
    .describe('Type of fuel delivered (e.g., "JET-A1", "AVGAS")'),
  safPercentage: z
    .number()
    .nullable()
    .optional()
    .describe('Percentage of Sustainable Aviation Fuel (SAF) in the uplift'),
  aircraftPlannedDeparture: z
    .number()
    .nullable()
    .optional()
    .describe("Planned departure fuel quantity (in aircraft's unit)"),
  priceValue: z
    .number()
    .nullable()
    .optional()
    .describe('Price per unit of fuel'),
  priceCurrency: z
    .string()
    .nullable()
    .optional()
    .describe('Currency of the fuel price (e.g., "USD", "EUR")'),
  paid: z.boolean().describe('Whether the fuel uplift has been paid for'),
  aircraftUnit: z
    .string()
    .describe('Unit of measurement used by the aircraft (e.g., "kg", "lb")'),
  createdAt: ZdateTime.describe(
    'Timestamp when this fuel uplift record was created',
  ),
  updatedAt: ZdateTime.describe(
    'Timestamp when this fuel uplift record was last updated',
  ),
});

export type TechlogFluidUplift = z.infer<typeof ZTechlogFluidUplift>;
export type TechlogFuelUplift = z.infer<typeof ZTechlogFuelUplifts>;
